import React from 'react';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';

/**
 * Componente de Información General del Estudiante
 * Muestra datos personales, contacto, dirección y responsable financiero
 * 
 * @param {Object} studentData - Datos del estudiante desde el backend
 */
function InformacionGeneral({ studentData }) {
  // Formatear fecha de nacimiento
  const formatearFecha = (fecha) => {
    if (!fecha) return 'No disponible';
    try {
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return fecha;
    }
  };

  // Datos personales desde el backend
  const datosPersonales = {
    fechaNacimiento: formatearFecha(studentData?.fechaNacimiento),
    lugarNacimiento: studentData?.lugarNacimiento || 'No disponible',
    genero: studentData?.sexo || 'No disponible',
    estadoCivil: studentData?.estadoCivil || 'No disponible',
    nacionalidad: studentData?.nacionalidad || 'No disponible',
    paisOrigen: studentData?.paisOrigen || 'No disponible'
  };

  // Información de contacto desde el backend
  const informacionContacto = {
    emailPersonal: studentData?.emailPersonal || 'No disponible',
    emailInstitucional: studentData?.emailInstitucional || studentData?.email || 'No disponible',
    telefonoMovil: studentData?.telefonoPrincipal || 'No disponible',
    telefonoEmergencia: studentData?.telefonoEmergencia || 'No disponible',
    contactoEmergencia: studentData?.contactoEmergencia || 'No disponible'
  };

  // Dirección desde el backend
  const direccion = {
    calleNumero: studentData?.calle 
      ? `${studentData.calle}${studentData.numeroExt ? ' #' + studentData.numeroExt : ''}${studentData.numeroInt ? ' Int. ' + studentData.numeroInt : ''}`
      : 'No disponible',
    colonia: studentData?.colonia || 'No disponible',
    ciudad: studentData?.ciudad || 'No disponible',
    estado: studentData?.estado || 'No disponible',
    codigoPostal: studentData?.codigoPostal ? `C.P. ${studentData.codigoPostal}` : 'No disponible',
    pais: studentData?.pais || 'México'
  };

  // Responsable financiero - TODO: Conectar cuando exista la tabla
  const responsableFinanciero = {
    nombreCompleto: 'Pendiente de registro',
    relacionEstudiante: 'Pendiente',
    telefonoPrincipal: 'Pendiente',
    telefonoMensajes: 'Pendiente',
    email: 'Pendiente',
    direccionFiscal: 'Pendiente',
    rfc: 'Pendiente'
  };

  return (
    <div className={styles.informacionGeneralContainer}>
      {/* Datos Personales */}
      <section className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>📋</span>
          <h3 className={styles.sectionTitle}>Información Personal</h3>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Fecha de nacimiento</span>
            <span className={styles.infoValue}>{datosPersonales.fechaNacimiento}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Lugar de nacimiento</span>
            <span className={styles.infoValue}>{datosPersonales.lugarNacimiento}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Género</span>
            <span className={styles.infoValue}>{datosPersonales.genero}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Estado Civil</span>
            <span className={styles.infoValue}>{datosPersonales.estadoCivil}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Nacionalidad</span>
            <span className={styles.infoValue}>{datosPersonales.nacionalidad}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>País de Origen</span>
            <span className={styles.infoValue}>{datosPersonales.paisOrigen}</span>
          </div>
        </div>
      </section>

      {/* Información de Contacto */}
      <section className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>📧</span>
          <h3 className={styles.sectionTitle}>Información de Contacto</h3>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email Personal</span>
            <span className={styles.infoValue}>{informacionContacto.emailPersonal}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email Institucional</span>
            <span className={styles.infoValue}>{informacionContacto.emailInstitucional}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Teléfono Principal</span>
            <span className={styles.infoValue}>{informacionContacto.telefonoMovil}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Teléfono de Emergencia</span>
            <span className={styles.infoValue}>{informacionContacto.telefonoEmergencia}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Contacto de Emergencia</span>
            <span className={styles.infoValue}>{informacionContacto.contactoEmergencia}</span>
          </div>
        </div>
      </section>

      {/* Dirección */}
      <section className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🏠</span>
          <h3 className={styles.sectionTitle}>Dirección</h3>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Calle y Número</span>
            <span className={styles.infoValue}>{direccion.calleNumero}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Colonia</span>
            <span className={styles.infoValue}>{direccion.colonia}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Ciudad</span>
            <span className={styles.infoValue}>{direccion.ciudad}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Estado</span>
            <span className={styles.infoValue}>{direccion.estado}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Código Postal</span>
            <span className={styles.infoValue}>{direccion.codigoPostal}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>País</span>
            <span className={styles.infoValue}>{direccion.pais}</span>
          </div>
        </div>
      </section>

      {/* Contacto de Pago (Responsable Financiero) - Pendiente */}
      <section className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💳</span>
          <h3 className={styles.sectionTitle}>Contacto de Pago (Responsable Financiero)</h3>
          <span className={styles.pendingBadge}>Pendiente de implementación</span>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Nombre Completo</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.nombreCompleto}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Relación con el Estudiante</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.relacionEstudiante}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Teléfono Principal</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.telefonoPrincipal}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.email}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>RFC</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.rfc}</span>
          </div>
          <div className={`${styles.infoItem} ${styles.fullWidth}`}>
            <span className={styles.infoLabel}>Dirección Fiscal</span>
            <span className={styles.infoValuePending}>{responsableFinanciero.direccionFiscal}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default InformacionGeneral;
